import { useEffect, useState, useMemo } from "react";
import { api, formatDate, daysFromNow } from "../lib/api";
import { PageHeader, StatusBadge, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { BellRing } from "lucide-react";
import { toast } from "sonner";

export default function Compliance() {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => { api.get("/compliance").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => (!statusFilter || r.status === statusFilter) && (!catFilter || r.category === catFilter));
  }, [rows, statusFilter, catFilter]);

  const remind = async (row) => {
    try { await api.post("/compliance/remind", row); toast.success(`Reminder sent to ${row.vendor_name}`); }
    catch { toast.error("Could not send reminder"); }
  };

  const cats = [...new Set((rows || []).map((r) => r.category))];

  return (
    <div className="animate-fade-in">
      <PageHeader title="Compliance Tracker" subtitle="Monitor vendor document expiry and verification status" />

      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-zinc-200">
          <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40 h-9" data-testid="compliance-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Valid">Valid</SelectItem>
              <SelectItem value="Expiring Soon">Expiring Soon</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={catFilter || "all"} onValueChange={(val) => setCatFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40 h-9" data-testid="compliance-cat-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {(statusFilter || catFilter) && (
            <button onClick={() => { setStatusFilter(""); setCatFilter(""); }} className="text-xs text-[#6366F1] hover:underline">Clear filters</button>
          )}
          <div className="ml-auto text-xs text-zinc-500">{filtered.length} documents</div>
        </div>

        {rows === null && <div className="p-4"><Skeleton className="h-64" /></div>}
        {rows && filtered.length === 0 && <div className="p-8"><Empty title="All clear" subtitle="No documents match the current filters" /></div>}

        {rows && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-5 py-3">Vendor</th>
                  <th className="px-5 py-3">Document</th>
                  <th className="px-5 py-3">Expiry</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const dd = daysFromNow(r.expiry_date);
                  return (
                    <tr key={i} className="border-b last:border-0 hover:bg-zinc-50" data-testid="compliance-row">
                      <td className="px-5 py-3 font-medium">{r.vendor_name}<div className="text-xs text-zinc-500">{r.category}</div></td>
                      <td className="px-5 py-3">{r.document_type}</td>
                      <td className="px-5 py-3">{formatDate(r.expiry_date)} <span className="text-xs text-zinc-500 ml-1">({dd != null && dd >= 0 ? `${dd}d left` : `${Math.abs(dd)}d ago`})</span></td>
                      <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => remind(r)} data-testid="compliance-remind"><BellRing className="w-3 h-3 mr-1" /> Send Reminder</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
